import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { EconomicCalendarEvent } from "../../hooks/useEconomicCalendar";
import { trimEconomicEventName } from "./economicEventName";
import {
  displayCountry,
  eventTime,
  formatEconomicValue,
  impactColorOf,
  isFinitely,
} from "./economicEventFormat";
import { panelBorder, panelSurface } from "./overviewPanel";

// The full calendar's card: one event cut for a GRID rather than a rail.
// The panel's row leads with the time because a rail reads top to bottom as
// a timeline; here the section already says the tier and the cards sit
// three abreast, so the NAME leads — it is what you scan a grid for — with
// the time and country as its eyebrow and the figures as its floor. Three
// lines at a fixed height, so a speech and a print tile the same lattice.

export const CARD_HEIGHT = 74;

const EconomicEventCard: React.FC<{
  event: EconomicCalendarEvent;
  /** Today's pending prints breathe until the actual lands. */
  pulse: boolean;
}> = ({ event, pulse }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const actualReleased = isFinitely(event.actual);
  const hasFigures =
    isFinitely(event.previous) || isFinitely(event.estimate) || actualReleased;
  const stats = [
    ...(isFinitely(event.previous)
      ? [
          {
            label: "Prev",
            value: formatEconomicValue(event.previous, event.unit),
          },
        ]
      : []),
    ...(isFinitely(event.estimate)
      ? [
          {
            label: "Est",
            value: formatEconomicValue(event.estimate, event.unit),
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        height: CARD_HEIGHT,
        px: 1.5,
        py: 1.125,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderRadius: 1.5,
        backgroundColor: panelSurface(isDark),
        border: `1px solid ${panelBorder(isDark)}`,
      }}
    >
      {/* Eyebrow: the pending pulse, the clock, the market */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: impactColorOf(event),
            flexShrink: 0,
            ...(pulse &&
              !actualReleased && {
                animation: "econDotPulse 2.2s ease-in-out infinite",
                "@keyframes econDotPulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                  "100%": { opacity: 1 },
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }),
          }}
        />
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            lineHeight: 1.3,
            color: theme.palette.text.secondary,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {eventTime(event)}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            lineHeight: 1.3,
            color: theme.palette.text.secondary,
          }}
        >
          {displayCountry(event)}
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 600,
          lineHeight: 1.3,
          letterSpacing: "-0.005em",
          color: theme.palette.text.primary,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {trimEconomicEventName(event.event)}
      </Typography>

      {/* Floor: Prev and Est read as one muted group; Act stands apart on
          the right — the only number here that is news. A speech has no
          figures and keeps the line empty so the lattice holds. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1.5,
          minWidth: 0,
          height: 14,
        }}
      >
        {hasFigures &&
          stats.map((stat) => (
            <Typography
              key={stat.label}
              sx={{
                fontSize: "0.625rem",
                lineHeight: 1.3,
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
              }}
            >
              {stat.label}{" "}
              <Box
                component="span"
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {stat.value}
              </Box>
            </Typography>
          ))}
        {hasFigures && <Box sx={{ flex: 1 }} />}
        {hasFigures && (
          <Typography
            sx={{
              fontSize: "0.625rem",
              lineHeight: 1.3,
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
            }}
          >
            Act{" "}
            <Box
              component="span"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 700,
                color: actualReleased
                  ? theme.palette.text.primary
                  : theme.palette.text.disabled,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatEconomicValue(event.actual, event.unit)}
            </Box>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(EconomicEventCard);
